import Vue from 'vue';
import Vuex from 'vuex';
import * as api from '../services/api';
import { emptySearchModel, emptyProfileModel } from '@/helpers/dataModels';

const standardApiHeaders = {
  'Content-Type': 'application/json'
};

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    authToken: localStorage.getItem('authToken') || null,
    user: null,
    googleApiLoaded: false,
    activeSearch: JSON.parse(JSON.stringify(emptySearchModel)),
    searchResults: [],
    activeCandidateProfile: {},
    activeChatUserId: '',
    activeChatUserProfile: {},
    isWelcomeTooltipOpen: false,
    areSearchFiltersActive: false
  },
  getters: {
    getUserAccount (state) {
      return state.authToken && state.user && state.user.account ? state.user.account : null;
    },
    isUserAccountActive (state) {
      return state.authToken && state.user && state.user.account && state.user.account.isAccountActive;
    },
    getUserProfile (state) {
      return state.authToken && state.user && state.user.profile ? state.user.profile : null;
    },
    loggedInUserType (state) {
      return state.authToken && state.user && state.user.account.user_type === 'candidate' ? 'candidate' : 'company';
    },
    isCandidateProfileCompleted (state) {
      if (
        state.authToken
        && state.user
        && state.user.account.user_type === 'candidate'
        && state.user.profile
      ) {
        return state.user.profile.isProfileCompleted;
      } else {
        return null;
      }
    },
    candidateProfileCreationCurrentStep (state) {
      if (
        state.authToken
        && state.user
        && state.user.account.user_type === 'candidate'
        && !state.user.profile.isProfileCompleted
      ) {
        return parseInt(state.user.profile.profileCreationCurrentStep);
      } else {
        return null;
      }
    },
    getAuthToken (state) {
      return state.authToken;
    },
    getAuthHeaders (state) {
      return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${state.authToken}` };
    },
    getCandidateCertifications (state) {
      if (state.user && state.user.profile) {
        return state.user.profile.education.certifications.map(obj => {
          return {
            type: obj.type === 'license',
            institution: obj.institution,
            certification_name: obj.certification_name,
            complete: obj.complete,
            active: obj.active
          };
        });
      }
    },
    // Get a computed search that resets all filters to empty while keeping them intact in state object.
    getSearchWithFilterStatus (state) {
      if (state.areSearchFiltersActive) {
        return { ...state.activeSearch };
      } else {
        return Object.assign({}, emptySearchModel, { search: state.activeSearch.search });
      }
    }
  },
  mutations: {
    /* LOGIN // AUTH // LOG OUT */
    LOGIN_SUCCESS (state, user) {
      state.authToken = localStorage.getItem('authToken');
      state.user = user;
    },
    LOGIN_FAILURE (state) {
      // No mutation here yet.
    },
    LOG_OUT (state) {
      localStorage.removeItem('authToken');
      state.authToken = null;
      state.user = null;
      /**
       * Commented out for the time being since we need to maintain
       * the profile creation step index even if the user logs out.
       * Bit of a code smell to be storing a session variable in localStorage
       * even after the user has ended their session. Ideal solution
       * is to store this on the backedn at some point.
       */
      // localStorage.removeItem('profileCreationIndex');
      // state.profileCreationCurrentStep = null;
    },
    /* EDIT CANDIDATE PROFILE */
    UPDATE_PROFILE_CREATION_STEP (state, newProfileCreationStep) {
      state.user.profile.profileCreationCurrentStep = newProfileCreationStep.toString();
    },
    MARK_PROFILE_COMPLETED (state, isProfileCompleted) {
      state.user.profile.isProfileCompleted = isProfileCompleted;
    },
    REFRESH_PROFILE (state, user) {
      state.user.profile = user.profile;
    },
    EDIT_CANDIDATE_PROFESSION (state, profession) {
      if (state.user && state.user.profile) {
        state.user.profile.profession = profession;
      }
    },
    EDIT_CANDIDATE_CURRENT_LOCATION (state, newLocation) {
      if (state.user && state.user.profile) {
        state.user.profile.location.current = newLocation;
      }
    },
    EDIT_CANDIDATE_RELOCATION (state, isOpenToRelocation) {
      if (state.user && state.user.profile) {
        state.user.profile.location.open_move = isOpenToRelocation;
      }
    },
    EDIT_CANDIDATE_UNDISCLOSE_LOCATION (state, isLocationUndisclosed) {
      if (state.user && state.user.profile) {
        state.user.profile.location.undisclosed = isLocationUndisclosed;
      }
    },
    EDIT_CANDIDATE_INDUSTRIES (state, industries) {
      if (state.user && state.user.profile) {
        state.user.profile.industries.current = industries;
      }
    },
    EDIT_CANDIDATE_SKILLS (state, skills) {
      if (state.user && state.user.profile) {
        state.user.profile.skills = skills;
      }
    },
    EDIT_CANDIDATE_DEGREES (state, degrees) {
      if (state.user && state.user.profile) {
        state.user.profile.education.degrees = degrees;
      }
    },
    EDIT_CANDIDATE_CERTIFICATIONS (state, certifications) {
      if (state.user && state.user.profile) {
        let certificationsWithTypeName = certifications.map(obj => {
          return {
            type: obj.type === true ? 'license' : 'certification',
            institution: obj.institution,
            certification_name: obj.certification_name,
            complete: obj.complete,
            active: obj.active
          };
        });
        state.user.profile.education.certifications = certificationsWithTypeName;
      }
    },
    EDIT_CANDIDATE_COMPANIES (state, companies) {
      if (state.user && state.user.profile) {
        state.user.profile.companies = companies;
      }
    },
    EDIT_CANDIDATE_BIO (state, bio) {
      if (state.user && state.user.profile) {
        state.user.profile.bio = bio;
      }
    },
    EDIT_CANDIDATE_ACCOMPLISHMENTS (state, accomplishments) {
      if (state.user && state.user.profile) {
        state.user.profile.accomplishments = [...accomplishments];
      }
    },
    UPDATE_CANDIDATE_PROFILE (state, profile) {
      if (state.user) {
        state.user.profile = { ...profile };
      }
    },
    /* SEARCH & SEE USER PROFILE */
    UPDATE_SEARCH_OBJECT (state, searchObject) {
      state.activeSearch = Object.assign({}, searchObject);
    },
    TOGGLE_SEARCH_FILTERS (state, newSearchFiltersState) {
      state.areSearchFiltersActive = newSearchFiltersState;
    },
    SET_ACTIVE_CANDIDATE_PROFILE (state, candidateProfile) {
      state.activeCandidateProfile = candidateProfile;
    },
    /* CHAT STATE MANIPULATION */
    SET_ACTIVE_CHAT_USER_ID (state, userId) {
      state.activeChatUserId = userId;
    },
    SET_ACTIVE_CHAT_USER_PROFILE (state, profile) {
      state.activeChatUserProfile = { ...profile };
    },
    /* MISC // HELPERS */
    GOOGLE_API_LOADED (state) {
      state.googleApiLoaded = true;
    },
    TOGGLE_WELCOME_TOOLTIP (state, newTooltipState) {
      state.isWelcomeTooltipOpen = newTooltipState;
    }
  },
  actions: {
    initApp (context) {
      return new Promise((resolve, reject) => {
        if (context.state.authToken) {
          api.getUserProfile(context.getters.getAuthHeaders)
            .then(response => {
              context.commit('LOGIN_SUCCESS', response.data.data);
              resolve(response);
            })
            .catch(error => {
              context.commit('LOG_OUT', error);
              reject(error);
            });
        } else {
          resolve('Logged Out');
        }
      });
    },
    refreshCandidateProfile (context) {
      return new Promise((resolve, reject) => {
        if (context.state.authToken) {
          api.getUserProfile(context.getters.getAuthHeaders)
            .then(response => {
              context.commit('REFRESH_PROFILE', response.data.data);
              resolve(response);
            })
            .catch(error => reject(error));
        } else {
          reject(new Error('Logged Out'));
        }
      });
    },
    signUpNewUser (context, userDetails) {
      return new Promise((resolve, reject) => {
        api.signUpNewUser(userDetails, standardApiHeaders)
          .then(response => {
            if (response.data.status !== 'success') {
              reject(response);
            } else {
              localStorage.setItem('authToken', response.data.token);
              context.commit('LOGIN_SUCCESS', response.data.data);
              if (response.data.data && response.data.data.account.user_type === 'company') {
                resolve(response);
              } else if (response.data.data && response.data.data.account.user_type === 'candidate') {
                context.dispatch('editCandidateProfile', 'isNewUser')
                  .then(response => {
                    if (response.data.status !== 'saved') {
                      reject(response);
                    } else {
                      context.commit('UPDATE_CANDIDATE_PROFILE', response.data.data.profile);
                      resolve(response);
                    }
                  })
                  .catch(error => reject(error));
              }
            }
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    logInUser (context, userDetails) {
      return new Promise((resolve, reject) => {
        api.signInUser(userDetails, standardApiHeaders)
          .then(response => {
            if (response.data.status !== 'success') {
              reject(response);
            } else {
              localStorage.setItem('authToken', response.data.token);
              context.commit('LOGIN_SUCCESS', response.data.data);
              resolve(response);
            }
          })
          .catch(error => reject(error));
      });
    },
    activateUserAccount (context, userDetails) {
      return new Promise((resolve, reject) => {
        api.activateUser(userDetails, standardApiHeaders)
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    reSendActivationEmail (context, userDetails) {
      return new Promise((resolve, reject) => {
        api.reSendActivationEmail(userDetails, context.getters.getAuthHeaders)
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    sendPasswordResetEmail (context, userDetails) {
      return new Promise((resolve, reject) => {
        api.sendResetPasswordEmail(userDetails, standardApiHeaders)
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },
    validatePasswordResetToken (context, userDetails) {
      return new Promise((resolve, reject) => {
        api.validatePasswordResetToken(userDetails, standardApiHeaders)
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    updateUserPassword (context, userDetails) {
      return new Promise((resolve, reject) => {
        api.updateUserPassword(userDetails, context.getters.getAuthHeaders)
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    editCandidateProfile (context, profile) {
      let currentProfile = profile === 'isNewUser' ? emptyProfileModel : profile;
      return new Promise((resolve, reject) => {
        api.editCandidateProfile(currentProfile, context.getters.getAuthHeaders)
          .then(response => {
            if (response.data.status !== 'saved') {
              reject(response);
            } else {
              resolve(response);
            }
          })
          .catch(error => reject(error));
      });
    },
    searchForCandidates (context) {
      return new Promise((resolve, reject) => {
        api.searchForCandidates(context.getters.getSearchWithFilterStatus, context.getters.getAuthHeaders)
          .then(response => {
            if (response.data.status !== 'success') {
              reject(response);
            } else {
              resolve(response);
            }
          })
          .catch(error => reject(error));
      });
    },
    handleToggleSearchFilters (context, newSearchFiltersState) {
      return new Promise((resolve, reject) => {
        context.commit('TOGGLE_SEARCH_FILTERS', newSearchFiltersState);

        context.dispatch('searchForCandidates')
          .then(() => {
            resolve();
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    getChatList (context, userDetails) {
      return new Promise((resolve, reject) => {
        api.getChatList(userDetails, context.getters.getAuthHeaders)
          .then(response => {
            resolve(response);
          })
          .catch(error => reject(error));
      });
    },
    getActiveChat (context, userDetails) {
      return new Promise((resolve, reject) => {
        api.getChatDetails(userDetails, context.getters.getAuthHeaders)
          .then(response => {
            resolve(response);
          })
          .catch(error => reject(error));
      });
    },
    getUnreadMessageCount (context, userDetails) {
      return new Promise((resolve, reject) => {
        api.getUnreadMessageCount(userDetails, context.getters.getAuthHeaders)
          .then(response => {
            resolve(response);
          })
          .catch(error => reject(error));
      });
    },
    getSkills (context, skillQuery) {
      return new Promise((resolve, reject) => {
        api.getSkills(context.getters.getAuthHeaders, skillQuery)
          .then(response => resolve(response))
          .catch(error => reject(error));
      });
    },
    getIndustries (context, industryQuery) {
      return new Promise((resolve, reject) => {
        api.getIndustries(context.getters.getAuthHeaders, industryQuery)
          .then(response => resolve(response))
          .catch(error => reject(error));
      });
    },
    getUniversities (context, universityQuery) {
      return new Promise((resolve, reject) => {
        api.getUniversities(context.getters.getAuthHeaders, universityQuery)
          .then(response => resolve(response))
          .catch(error => reject(error));
      });
    },
    updateProfileCreationStep (context, newProfileCreationStep) {
      if (newProfileCreationStep <= 8) {
        context.commit('UPDATE_PROFILE_CREATION_STEP', newProfileCreationStep);
      } else if (newProfileCreationStep > 8) {
        context.commit('UPDATE_PROFILE_CREATION_STEP', newProfileCreationStep);
        context.commit('MARK_PROFILE_COMPLETED', true);
      }
    }
  }
});
